import useSWR from 'swr'
import {
  FasDataItem,
  FasDataItemPlanningStatus,
  FasDataStatsEntry,
  FasDataItemBase,
  FasData,
  NormalizedSearchWords,
} from '../types'
import { TabItem } from '../components/Tabs'
import { IS_STORYBOOK } from '../../../../utils/constants'
import { mockFasData } from './driftsinfoData.mock'

const nppFasEndpoint = '/api/driftsinfo/getfasdata'

export const map = <T, TVal>(val: TVal, mapFn: (arg: TVal) => T) => mapFn(val)

export const wellKnownGeography = (() => {
  const values = ['Danmark', 'Sjælland', 'Jylland', 'Fyn', 'Bornholm', 'Flere danske områder'] as const
  const allValueFlags = Array.from(values.keys(), k => [k, 1 << k])
  const allFlag = ~(~0 << values.length)

  type WellKnownGeographyValue = (typeof values)[number]

  const calcFlag = (geography: string): number =>
    map(values.indexOf(geography as WellKnownGeographyValue), idx => (idx === -1 ? 0 : 1 << idx))

  const isFlagged = (flags: number, containsFlags: number): boolean | undefined =>
    (flags & containsFlags) === containsFlags

  const getFlaggedValues = (flags: number): WellKnownGeographyValue[] =>
    flags === 0 ? [] : allValueFlags.filter(([_, k]) => (flags & k) === k).map(([idx]) => values[idx])

  const isValueFlagged = (flags: number, geography: WellKnownGeographyValue): boolean | undefined =>
    isFlagged(flags, calcFlag(geography))

  const getFlaggedOrAllValues = (flags: number): WellKnownGeographyValue[] =>
    map(getFlaggedValues(flags), vals => (vals.length ? vals : [...values]))

  return {
    values,
    allFlag,
    calcFlag,
    isFlagged,
    isValueFlagged,
    getFlaggedOrAllValues,
  } as const
})()

export type FasDataStats = {
  readonly [K in (typeof wellKnownGeography.values)[number]]: FasDataStatsEntry
}

export const paginateData = (data: FasDataItemBase[], page = 1, perPage = 8) =>
  data?.slice(perPage * (page - 1), perPage * page)

export const createEmptyFasDataStatsEntry = (): FasDataStatsEntry => ({
  [FasDataItemPlanningStatus.Unplanned]: 0,
  [FasDataItemPlanningStatus.Planned]: 0,
  [FasDataItemPlanningStatus.Completed]: 0,
  total: 0,
})

export function createEmptyFasDataStats(): FasDataStats {
  return {
    Danmark: createEmptyFasDataStatsEntry(),
    Sjælland: createEmptyFasDataStatsEntry(),
    Jylland: createEmptyFasDataStatsEntry(),
    Fyn: createEmptyFasDataStatsEntry(),
    Bornholm: createEmptyFasDataStatsEntry(),
    'Flere danske områder': createEmptyFasDataStatsEntry(),
  }
}

export function calculateFasDataStats(items: readonly FasDataItem[]) {
  return items.reduce<FasDataStats & { [index: string]: FasDataStatsEntry | undefined }>((acc, cur) => {
    const statsEntry = acc[cur.geography] ?? createEmptyFasDataStatsEntry()
    statsEntry[cur.planningStatus] += 1
    statsEntry.total += 1
    return {
      ...acc,
      [cur.geography]: statsEntry,
    }
  }, createEmptyFasDataStats())
}

export const getTabsWithCounts = (
  stats: FasData['stats'] | undefined,
  selectedGeoFilters: number,
): TabItem<FasDataItemPlanningStatus>[] => {
  const effectiveGeoFilters = wellKnownGeography.getFlaggedOrAllValues(selectedGeoFilters)

  const createTab = (planningStatus: FasDataItemPlanningStatus, label: string) => ({
    id: planningStatus,
    label,
    badgeContent: stats
      ? effectiveGeoFilters.map(gv => stats[gv][planningStatus]).reduce((a, b) => a + b, 0)
      : undefined,
  })

  return [
    createTab(FasDataItemPlanningStatus.Unplanned, 'Aktuelle'),
    createTab(FasDataItemPlanningStatus.Planned, 'Planlagt'),
    createTab(FasDataItemPlanningStatus.Completed, 'Løste'),
  ]
}

export const normalizeString = (str: string) =>
  str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()

export const splitToNormalizedWords = (wordsString: string): NormalizedSearchWords =>
  normalizeString(wordsString)
    .trim()
    .split(' ')
    .filter(s => s.length > 1)

export function includesAnyOfFullWords(item: FasDataItem, searchWords: NormalizedSearchWords): boolean {
  return searchWords.length === 0
    ? true
    : searchWords.some(
        searchWords =>
          normalizeString(item.reason).includes(searchWords) ||
          normalizeString(item.title).includes(searchWords) ||
          normalizeString(item.consequence).includes(searchWords),
      )
}

const fasDataFetcher = async (url: string): Promise<FasData> => {
  return fetch(url)
    .then(response => response.json())
    .then(dtos => {
      const all: FasDataItem[] = dtos
        .filter(
          (dto: FasDataItem) => ['Mobil Telefoni', 'Mobil Tjenester', 'Telefoni'].indexOf(dto.function.trim()) > -1,
        )
        .map((dto: FasDataItem) => {
          const item = {
            ...dto,
            title: dto.title.trim(),
            geography: dto.geography,
            geographyFlag: wellKnownGeography.calcFlag(dto.geography),
          }

          const planningStatus = dto.realResolved
            ? FasDataItemPlanningStatus.Completed
            : dto.plannedStart
              ? FasDataItemPlanningStatus.Planned
              : FasDataItemPlanningStatus.Unplanned

          return planningStatus !== FasDataItemPlanningStatus.Completed
            ? { ...item, planningStatus }
            : {
                ...item,
                planningStatus,
                wasPlanned: !!dto.plannedStart,
              }
        })

      return {
        all,
        stats: calculateFasDataStats(all),
      }
    })
}

export const useGetFasDataHook = () => {
  const { data, error } = useSWR(nppFasEndpoint, fasDataFetcher)

  if (IS_STORYBOOK) {
    return {
      fasData: mockFasData,
      fasDataFetchError: undefined,
    }
  }

  return {
    fasData: data,
    fasDataFetchError: error,
    isLoadingFasData: !data && !error,
  }
}
